import { arrowHeadGeometry } from '../lib/connector-geometry';
import { createId } from '../lib/ids';
import {
  CURRENT_PROJECT_SCHEMA,
  getMarkupLayer,
  getPhotoLayer,
  type DrawElement,
  type FreeformShapeElement,
  type ImageElement,
  type LineElement,
  type MarkupElement,
  type NumberElement,
  type PolylineShapeElement,
  type ProjectAsset,
  type RealImagerProject,
  type ShapeElement,
  type TextElement
} from '../types/project';
import { projectStore } from './project-store';

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

export function isSupportedImage(file: File): boolean {
  return IMAGE_TYPES.includes(file.type.toLowerCase()) || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
}

export async function normalizeImageFile(file: File): Promise<File> {
  if (!/\.(heic|heif)$/i.test(file.name) && !['image/heic', 'image/heif'].includes(file.type)) return file;
  const { default: heic2any } = await import('heic2any');
  const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.95 });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
}

export async function decodeImage(file: File): Promise<{ file: File; bitmap: ImageBitmap }> {
  const normalized = await normalizeImageFile(file);
  const bitmap = await createImageBitmap(normalized, { imageOrientation: 'from-image' });
  return { file: normalized, bitmap };
}

export async function createProjectFromFile(sourceFile: File): Promise<RealImagerProject> {
  const projectId = createId('project');
  const { assets, workingAsset: asset, bitmap } = await createProjectAssets(sourceFile, projectId, 'source');
  const now = new Date().toISOString();
  const thumbnailDataUrl = createThumbnail(bitmap);
  bitmap.close();
  const project: RealImagerProject = {
    schemaVersion: CURRENT_PROJECT_SCHEMA,
    id: projectId,
    name: sourceFile.name.replace(/\.[^.]+$/, ''),
    createdAt: now,
    updatedAt: now,
    canvas: { width: asset.width, height: asset.height, background: '#ffffff' },
    assets,
    layers: [
      { id: createId('layer'), type: 'raster', name: 'Photo', visible: true, locked: true, zIndex: 0, assetId: asset.id },
      { id: createId('layer'), type: 'markup', name: 'Objects', visible: true, locked: false, zIndex: 1, elements: [] }
    ],
    exportDefaults: { format: 'jpeg', quality: 0.92, preserveMetadata: true },
    thumbnailDataUrl
  };
  await projectStore.save(project);
  return project;
}

export async function createProjectAssets(
  sourceFile: File,
  projectId: string,
  role: 'source' | 'imported-source'
): Promise<{ assets: ProjectAsset[]; workingAsset: ProjectAsset; bitmap: ImageBitmap }> {
  if (!isSupportedImage(sourceFile)) throw new Error('Choose a JPEG, PNG, WebP, HEIC, or HEIF image.');
  const { file, bitmap } = await decodeImage(sourceFile);
  const now = new Date().toISOString();
  const workingId = createId('asset');
  const workingKey = `${projectId}/${workingId}`;
  await projectStore.putAsset(workingKey, file);
  const workingAsset: ProjectAsset = {
    id: workingId,
    name: file.name,
    mimeType: file.type,
    byteLength: file.size,
    width: bitmap.width,
    height: bitmap.height,
    storageKey: workingKey,
    createdAt: now,
    role: file === sourceFile ? role : role === 'source' ? 'working' : 'imported-working'
  };
  if (file === sourceFile) return { assets: [workingAsset], workingAsset, bitmap };
  const sourceId = createId('asset');
  const sourceKey = `${projectId}/${sourceId}`;
  await projectStore.putAsset(sourceKey, sourceFile);
  const original: ProjectAsset = {
    id: sourceId,
    name: sourceFile.name,
    mimeType: sourceFile.type || 'image/heic',
    byteLength: sourceFile.size,
    width: bitmap.width,
    height: bitmap.height,
    storageKey: sourceKey,
    createdAt: now,
    role
  };
  return { assets: [original, workingAsset], workingAsset, bitmap };
}

function createThumbnail(bitmap: ImageBitmap): string {
  const max = 480;
  const ratio = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * ratio));
  canvas.height = Math.max(1, Math.round(bitmap.height * ratio));
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.76);
}

export async function projectAssetUrls(project: RealImagerProject): Promise<Record<string, string>> {
  const urls: Record<string, string> = {};
  for (const asset of project.assets) {
    const blob = await projectStore.getAsset(asset.storageKey);
    if (blob) urls[asset.id] = URL.createObjectURL(blob);
  }
  return urls;
}

export async function exportProjectImage(
  project: RealImagerProject,
  format: 'jpeg' | 'png' | 'webp',
  quality: number,
  preserveMetadata = project.exportDefaults.preserveMetadata
): Promise<Blob> {
  if (project.canvas.width * project.canvas.height > 100_000_000) {
    throw new Error('This image exceeds the 100 MP export limit.');
  }
  const canvas = await renderProjectCanvas(project);
  const mime = format === 'jpeg' ? 'image/jpeg' : `image/${format}`;
  let output = await canvasToBlob(canvas, mime, quality);
  const photo = getPhotoLayer(project);
  const asset = project.assets.find((item) => item.id === photo.assetId);
  const source = asset ? await projectStore.getAsset(asset.storageKey) : undefined;
  if (preserveMetadata && source?.type === mime) output = await preserveCompatibleMetadata(source, output, mime);
  return output;
}

export async function renderProjectCanvas(project: RealImagerProject): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = project.canvas.width;
  canvas.height = project.canvas.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Image rendering is unavailable.');
  context.fillStyle = project.canvas.background;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const photo = getPhotoLayer(project);
  const photoAsset = project.assets.find((item) => item.id === photo.assetId);
  const source = photoAsset ? await projectStore.getAsset(photoAsset.storageKey) : undefined;
  if (!source) throw new Error('Project photo is unavailable.');
  const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' });
  try {
    if (photo.visible) context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  } finally {
    bitmap.close();
  }

  const elements = getMarkupLayer(project).elements
    .filter((element) => element.visible)
    .sort((a, b) => a.zIndex - b.zIndex);
  for (const element of elements) await renderElement(context, project, element);
  return canvas;
}

export async function refreshProjectThumbnail(project: RealImagerProject): Promise<string> {
  const rendered = await renderProjectCanvas(project);
  const max = 480;
  const ratio = Math.min(1, max / Math.max(rendered.width, rendered.height));
  const thumbnail = document.createElement('canvas');
  thumbnail.width = Math.max(1, Math.round(rendered.width * ratio));
  thumbnail.height = Math.max(1, Math.round(rendered.height * ratio));
  thumbnail.getContext('2d')?.drawImage(rendered, 0, 0, thumbnail.width, thumbnail.height);
  return thumbnail.toDataURL('image/jpeg', 0.76);
}

async function renderElement(context: CanvasRenderingContext2D, project: RealImagerProject, element: MarkupElement): Promise<void> {
  if (element.type === 'image') return renderImageElement(context, project, element);
  context.save();
  context.globalAlpha = element.opacity;
  const bounds = pixelBounds(context.canvas, element.bounds);
  context.translate(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2);
  context.rotate(element.rotation * Math.PI / 180);
  context.translate(-(bounds.x + bounds.width / 2), -(bounds.y + bounds.height / 2));
  if (element.type === 'rect' || element.type === 'ellipse' || element.type === 'polygon' || element.type === 'star') renderShape(context, element, bounds);
  else if (element.type === 'freeform' || element.type === 'polyline') renderFreeformShape(context, element);
  else if (element.type === 'line' || element.type === 'arrow') renderLine(context, element);
  else if (element.type === 'pencil' || element.type === 'marker' || element.type === 'brush' || element.type === 'pen') renderDrawPath(context, element);
  else if (element.type === 'text') renderText(context, element, bounds);
  else if (element.type === 'number') renderNumber(context, element, bounds);
  context.restore();
}

function renderShape(context: CanvasRenderingContext2D, element: ShapeElement, bounds: PixelBounds): void {
  context.beginPath();
  if (element.type === 'rect') {
    context.roundRect(bounds.x, bounds.y, bounds.width, bounds.height, Math.min(element.cornerRadius, bounds.width / 2, bounds.height / 2));
  } else if (element.type === 'ellipse') {
    context.ellipse(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2, bounds.width / 2, bounds.height / 2, 0, 0, Math.PI * 2);
  } else if (element.type === 'polygon') {
    const sides = Math.max(3, Math.min(12, Math.round(element.sides)));
    const cx = bounds.x + bounds.width / 2;
    const cy = bounds.y + bounds.height / 2;
    for (let index = 0; index < sides; index += 1) {
      const angle = -Math.PI / 2 + index * Math.PI * 2 / sides;
      const x = cx + Math.cos(angle) * bounds.width / 2;
      const y = cy + Math.sin(angle) * bounds.height / 2;
      index ? context.lineTo(x, y) : context.moveTo(x, y);
    }
    context.closePath();
  } else {
    const points = Math.max(3, Math.min(12, Math.round(element.sides)));
    const cx = bounds.x + bounds.width / 2;
    const cy = bounds.y + bounds.height / 2;
    for (let index = 0; index < points * 2; index += 1) {
      const angle = -Math.PI / 2 + index * Math.PI / points;
      const radius = index % 2 === 0 ? 1 : .43;
      const x = cx + Math.cos(angle) * bounds.width / 2 * radius;
      const y = cy + Math.sin(angle) * bounds.height / 2 * radius;
      index ? context.lineTo(x, y) : context.moveTo(x, y);
    }
    context.closePath();
  }
  if (element.fillColor) {
    context.save();
    context.globalAlpha *= element.fillOpacity;
    context.fillStyle = element.fillColor;
    context.fill();
    context.restore();
  }
  context.strokeStyle = element.strokeColor;
  context.lineWidth = element.strokeWidth;
  context.stroke();
}

function renderFreeformShape(context: CanvasRenderingContext2D, element: FreeformShapeElement | PolylineShapeElement): void {
  if (element.points.length < 3) return;
  context.beginPath();
  element.points.forEach((point, index) => {
    const x = point.x * context.canvas.width;
    const y = point.y * context.canvas.height;
    index ? context.lineTo(x, y) : context.moveTo(x, y);
  });
  context.closePath();
  if (element.fillColor) {
    context.save();
    context.globalAlpha *= element.fillOpacity;
    context.fillStyle = element.fillColor;
    context.fill();
    context.restore();
  }
  context.strokeStyle = element.strokeColor;
  context.lineWidth = element.strokeWidth;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.stroke();
}

function renderLine(context: CanvasRenderingContext2D, element: LineElement): void {
  const width = context.canvas.width;
  const height = context.canvas.height;
  const start = { x: element.points[0].x * width, y: element.points[0].y * height };
  const end = { x: element.points[1].x * width, y: element.points[1].y * height };
  const control = element.controlPoint ? { x: element.controlPoint.x * width, y: element.controlPoint.y * height } : null;
  context.strokeStyle = element.strokeColor;
  context.fillStyle = element.strokeColor;
  context.lineWidth = element.strokeWidth;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.beginPath();
  context.moveTo(start.x, start.y);
  if (element.arrowStyle === 'curved' && control) context.quadraticCurveTo(control.x, control.y, end.x, end.y);
  else context.lineTo(end.x, end.y);
  context.stroke();
  if (element.type === 'arrow') {
    const startVector = element.arrowStyle === 'curved' && control ? { x: control.x - start.x, y: control.y - start.y } : { x: end.x - start.x, y: end.y - start.y };
    const endVector = element.arrowStyle === 'curved' && control ? { x: end.x - control.x, y: end.y - control.y } : startVector;
    if (element.arrowEnds === 'start' || element.arrowEnds === 'both') drawArrowHead(context, start, { x: -startVector.x, y: -startVector.y }, element.strokeWidth);
    if (element.arrowEnds === 'end' || element.arrowEnds === 'both') drawArrowHead(context, end, endVector, element.strokeWidth);
  }
}

function drawArrowHead(context: CanvasRenderingContext2D, point: { x: number; y: number }, vector: { x: number; y: number }, strokeWidth: number): void {
  const head = arrowHeadGeometry(point, vector, Math.max(18, strokeWidth * 5));
  context.beginPath();
  context.moveTo(head.tip.x, head.tip.y);
  context.lineTo(head.left.x, head.left.y);
  context.lineTo(head.right.x, head.right.y);
  context.closePath();
  context.fill();
}

function renderDrawPath(context: CanvasRenderingContext2D, element: DrawElement): void {
  if (element.points.length < 2) return;
  context.strokeStyle = element.strokeColor;
  context.lineWidth = element.strokeWidth;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  if (element.type === 'marker') context.globalAlpha *= 0.38;
  context.beginPath();
  element.points.forEach((point, index) => {
    const x = point.x * context.canvas.width;
    const y = point.y * context.canvas.height;
    index ? context.lineTo(x, y) : context.moveTo(x, y);
  });
  if (element.closed) context.closePath();
  context.stroke();
}

function renderText(context: CanvasRenderingContext2D, element: TextElement, bounds: PixelBounds): void {
  context.font = `${element.fontWeight} ${element.fontSize}px ${element.fontFamily}`;
  context.textBaseline = 'middle';
  context.textAlign = element.textAlign;
  const anchorX = element.textAlign === 'left' ? bounds.x : element.textAlign === 'right' ? bounds.x + bounds.width : bounds.x + bounds.width / 2;
  const anchorY = bounds.y + bounds.height / 2;
  if (element.backgroundColor) {
    context.fillStyle = element.backgroundColor;
    context.beginPath();
    context.roundRect(bounds.x, bounds.y, bounds.width, bounds.height, Math.min(10, bounds.height / 4));
    context.fill();
  }
  if (Math.abs(element.curve) < 1) {
    if (element.outlineColor) {
      context.strokeStyle = element.outlineColor;
      context.lineWidth = Math.max(2, element.fontSize * 0.08);
      context.strokeText(element.text, anchorX, anchorY);
    }
    context.fillStyle = element.color;
    context.fillText(element.text, anchorX, anchorY);
    return;
  }
  drawArcText(context, element, bounds);
}

function renderNumber(context: CanvasRenderingContext2D, element: NumberElement, bounds: PixelBounds): void {
  const centerX = bounds.x + bounds.width / 2;
  const centerY = bounds.y + bounds.height / 2;
  const radius = Math.min(bounds.width, bounds.height) / 2;
  if (element.backgroundColor) {
    context.fillStyle = element.backgroundColor;
    context.beginPath();
    context.arc(centerX, centerY, radius, 0, Math.PI * 2);
    context.fill();
  }
  context.fillStyle = element.color;
  context.font = `${element.fontWeight} ${element.fontSize}px ${element.fontFamily}`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(String(element.number), centerX, centerY);
}

function drawArcText(context: CanvasRenderingContext2D, element: TextElement, bounds: PixelBounds): void {
  const characters = Array.from(element.text);
  if (!characters.length) return;
  const curve = Math.max(-100, Math.min(100, element.curve));
  const direction = curve >= 0 ? -1 : 1;
  const radius = Math.max(bounds.width * 0.52, element.fontSize * 1.5) * (1.4 - Math.min(1, Math.abs(curve) / 100) * 0.55);
  const arc = Math.min(Math.PI * 0.95, Math.max(0.12, Math.abs(curve) / 100 * Math.PI * 0.9));
  const cx = bounds.x + bounds.width / 2;
  const cy = direction < 0 ? bounds.y + radius : bounds.y + bounds.height - radius;
  context.fillStyle = element.color;
  characters.forEach((character, index) => {
    const progress = characters.length === 1 ? 0.5 : index / (characters.length - 1);
    const angle = -Math.PI / 2 - arc / 2 + progress * arc;
    const x = cx + Math.cos(angle) * radius;
    const y = cy + Math.sin(angle) * radius * direction;
    context.save();
    context.translate(x, y);
    context.rotate(angle + Math.PI / 2);
    if (element.outlineColor) {
      context.strokeStyle = element.outlineColor;
      context.lineWidth = Math.max(2, element.fontSize * 0.08);
      context.strokeText(character, 0, 0);
    }
    context.fillText(character, 0, 0);
    context.restore();
  });
}

async function renderImageElement(context: CanvasRenderingContext2D, project: RealImagerProject, element: ImageElement): Promise<void> {
  const asset = project.assets.find((item) => item.id === element.assetId);
  const source = asset ? await projectStore.getAsset(asset.storageKey) : undefined;
  if (!source) return;
  const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' });
  const bounds = pixelBounds(context.canvas, element.bounds);
  try {
    context.save();
    context.globalAlpha = element.opacity;
    context.translate(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2);
    context.rotate(element.rotation * Math.PI / 180);
    const sourceX = Math.max(0, Math.min(bitmap.width - 1, element.crop.x * bitmap.width));
    const sourceY = Math.max(0, Math.min(bitmap.height - 1, element.crop.y * bitmap.height));
    const sourceWidth = Math.max(1, Math.min(bitmap.width - sourceX, element.crop.width * bitmap.width));
    const sourceHeight = Math.max(1, Math.min(bitmap.height - sourceY, element.crop.height * bitmap.height));
    context.drawImage(bitmap, sourceX, sourceY, sourceWidth, sourceHeight, -bounds.width / 2, -bounds.height / 2, bounds.width, bounds.height);
    context.restore();
  } finally {
    bitmap.close();
  }
}

interface PixelBounds { x: number; y: number; width: number; height: number }

function pixelBounds(canvas: HTMLCanvasElement, bounds: { x: number; y: number; width: number; height: number }): PixelBounds {
  return { x: bounds.x * canvas.width, y: bounds.y * canvas.height, width: bounds.width * canvas.width, height: bounds.height * canvas.height };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Unable to encode the image.')), type, quality));
}

async function preserveCompatibleMetadata(source: Blob, encoded: Blob, mime: string): Promise<Blob> {
  if (mime === 'image/jpeg') return preserveJpegSegments(source, encoded);
  if (mime === 'image/png') return preservePngChunks(source, encoded);
  return encoded;
}

async function preserveJpegSegments(source: Blob, encoded: Blob): Promise<Blob> {
  const input = new Uint8Array(await source.arrayBuffer());
  const output = new Uint8Array(await encoded.arrayBuffer());
  if (input[0] !== 0xff || input[1] !== 0xd8 || output[0] !== 0xff || output[1] !== 0xd8) return encoded;
  const segments: Uint8Array[] = [];
  let offset = 2;
  while (offset + 4 < input.length && input[offset] === 0xff) {
    const marker = input[offset + 1];
    if (marker === 0xda || marker === 0xd9) break;
    const length = (input[offset + 2] << 8) | input[offset + 3];
    if (length < 2 || offset + length + 2 > input.length) break;
    if (marker >= 0xe1 && marker <= 0xed) segments.push(input.slice(offset, offset + length + 2));
    offset += length + 2;
  }
  return segments.length ? new Blob([output.slice(0, 2), ...segments, output.slice(2)].map(toArrayBuffer), { type: 'image/jpeg' }) : encoded;
}

async function preservePngChunks(source: Blob, encoded: Blob): Promise<Blob> {
  const input = new Uint8Array(await source.arrayBuffer());
  const output = new Uint8Array(await encoded.arrayBuffer());
  const signature = [137, 80, 78, 71, 13, 10, 26, 10];
  if (!signature.every((value, index) => input[index] === value && output[index] === value)) return encoded;
  const chunks: Uint8Array[] = [];
  let offset = 8;
  while (offset + 12 <= input.length) {
    const length = new DataView(input.buffer, input.byteOffset + offset, 4).getUint32(0);
    const type = new TextDecoder().decode(input.slice(offset + 4, offset + 8));
    const end = offset + length + 12;
    if (end > input.length) break;
    if (!['IHDR', 'PLTE', 'IDAT', 'IEND'].includes(type)) chunks.push(input.slice(offset, end));
    offset = end;
    if (type === 'IEND') break;
  }
  if (!chunks.length) return encoded;
  const firstChunkEnd = 8 + 12 + new DataView(output.buffer, output.byteOffset + 8, 4).getUint32(0);
  return new Blob([output.slice(0, firstChunkEnd), ...chunks, output.slice(firstChunkEnd)].map(toArrayBuffer), { type: 'image/png' });
}

function toArrayBuffer(view: Uint8Array): ArrayBuffer {
  return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
